import { CheckIcon } from '@chakra-ui/icons';
import { Button, FormControl, FormLabel, Input, InputGroup, InputLeftAddon, InputRightAddon, Skeleton, useBoolean, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import AddonIconButton from 'components/form-elements/AddonIconButton';
import AlertModal from 'components/modals/AlertModal';
import { TotpCountdownRing, totpCountdownRingSize } from 'components/TotpCountdownRing';
import { queryClient } from 'configs/queryClient';
import { useUserContext } from 'context/UserContextProvider';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useResetTotpSecretMutation from 'hooks/mutations/useResetTotpSecretMutation';
import useAccountDetailQuery from 'hooks/useAccountDetailQuery';
import useTotpCodeQuery from 'hooks/useTotpCodeQuery';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosCopy } from 'react-icons/io';
import { useCopyToClipboard } from 'usehooks-ts';

const showCheckIconDuration = 700;

type Props = {
  account_id: number;
};

export const TotpCodeDisplay = ({ account_id, hasTotpSecret }: Props & { hasTotpSecret: boolean }) => {
  const { t } = useTranslation();
  const [value, copy] = useCopyToClipboard();
  const [showCheckIcon, setShowCheckIcon] = useBoolean(false);

  const totpCodeQuery = useTotpCodeQuery(account_id, hasTotpSecret);
  const totpCode = totpCodeQuery.data?.data;

  return (
    <FormControl>
      <FormLabel size="lg">{t('components:totp_code.label')}</FormLabel>
      <InputGroup>
        <InputLeftAddon p={1} justifyContent="center">
          {totpCode ? (
            <TotpCountdownRing generatedAt={totpCode.generated_at} lifetimeSeconds={totpCode.lifetime_seconds} />
          ) : (
            <Skeleton boxSize={`${totpCountdownRingSize}px`} borderRadius="full" />
          )}
        </InputLeftAddon>
        <Input
          value={totpCode?.code ?? ''}
          placeholder="------"
          readOnly
          letterSpacing="widest"
          textAlign="center"
          animation={totpCode ? 'totp-code-blink 1s ease-in-out infinite' : undefined}
        />
        <InputRightAddon p={0}>
          <AddonIconButton
            icon={value === totpCode?.code && showCheckIcon ? <CheckIcon /> : <IoIosCopy />}
            aria-label={'copy 2fa code button'}
            isDisabled={!totpCode?.code}
            onClick={() => {
              if (!totpCode?.code) {
                return;
              }
              setShowCheckIcon.on();
              copy(totpCode.code);
              setTimeout(setShowCheckIcon.off, showCheckIconDuration);
            }}
          />
        </InputRightAddon>
      </InputGroup>
    </FormControl>
  );
};

export const TotpResetButton = ({ account_id }: Props) => {
  const { t } = useTranslation();
  const { isMember } = useUserContext();
  const toast = useToast(defaultToastOptions);
  const resetTotpSecretMutation = useResetTotpSecretMutation(account_id);

  useEffect(() => {
    if (resetTotpSecretMutation.isError) {
      toast({ title: resetTotpSecretMutation.error.message ?? t('errors:general'), status: 'error' });
    }
  }, [resetTotpSecretMutation.isError, resetTotpSecretMutation.error, t, toast]);

  if (!isMember('totp:reset-secret')) {
    return null;
  }

  const onClick = () => {
    NiceModal.show(AlertModal, {
      onSubmit: () =>
        resetTotpSecretMutation
          .mutateAsync(undefined, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: useAccountDetailQuery.getKey(account_id) });
              // removeQueries (not invalidateQueries) -- the code display is still mounted/enabled at this
              // instant (React hasn't re-rendered with the updated has_totp_secret yet), so invalidating would
              // force an immediate refetch against a secret that's already been cleared server-side, causing a 400.
              queryClient.removeQueries({ queryKey: useTotpCodeQuery.getKey(account_id) });
            },
          })
          // errors are surfaced via the isError effect above, not AlertModal's own inline error display
          .catch(() => undefined),
      children: t('components:totp_code.reset_confirm.text'),
      content: {
        header: t('components:totp_code.reset_confirm.header'),
        footer: {
          buttons: {
            cancelCaption: t('common:cancel'),
            actionCaption: t('components:totp_code.reset_button'),
          },
        },
      },
      redSubmitBttn: true,
    });
  };

  return (
    <Button
      variant="outline"
      size="sm"
      color="red.500"
      borderColor="red.500"
      _hover={{ bg: 'red.500', color: 'white' }}
      _active={{ bg: 'red.700', color: 'white' }}
      isDisabled={resetTotpSecretMutation.isPending}
      onClick={onClick}
      data-test-id="totp-reset-button"
    >
      {t('components:totp_code.reset_button')}
    </Button>
  );
};
